import { getErrorMessage } from "@/helpers/handleErrorResponse";
import { removeEmptyParams } from "@/helpers/objectHelpers";
import { removeStartFromUnderscore } from "@/helpers/string";
import {
  AddUpdateRowsDatasetPayload,
  AttributeOption,
  DatasetAnalysisPayload,
  DatasetData,
  DatasetFilterPayload,
  DatasetMetadata,
  DatasetMetaDataPayload,
  DatasetSchemaPayload,
  DatasetStatsRequestPayload,
  DatasetStatsResponse,
  ExportDatasetFileType,
  GetDatasetListPayload,
  GetDatasetListResponse,
  H3Configuration,
  OperationStatus,
  TranslateDatasetGeometryPayload
} from "@/types/dataset";
import { CustomError } from "@/utils/errors";

import { AxiosError, AxiosResponse } from "axios";
import FileSaver from "file-saver";

import axiosInstance from "../config";
import { fetchDatasetStream } from "./fetchDatasetStream";

type FetchDatasetStreamProps = Parameters<typeof fetchDatasetStream>[0];

export const getDatasetList = async (
  payload: GetDatasetListPayload
): Promise<GetDatasetListResponse> => {
  try {
    const { data } = await axiosInstance.get(`/v1/datasets`, {
      params: removeEmptyParams(payload)
    });
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const getDatasetMetadata = async (
  id: string,
  h3Configuration?: H3Configuration | null,
  show_id: boolean = false,
  group_by?: string
): Promise<DatasetMetadata> => {
  const additionalParams = {
    ...(h3Configuration
      ? {
          grid_aggregation_type: h3Configuration.aggregationType,
          grid_level: h3Configuration.resolution ?? 8
        }
      : {}),
    ...(show_id ? { show_id: true } : {}),
    ...(group_by && group_by !== "none" ? { group_by } : {})
  };

  try {
    const { data } = await axiosInstance.get(`/v1/datasets/${id}`, {
      params: additionalParams
    });
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const translateDatasetGeometry = async (
  id: string,
  payload: TranslateDatasetGeometryPayload
) => {
  try {
    const { data } = await axiosInstance.post(
      `/v1/datasets/${id}/schema/geocode`,
      payload
    );
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const datasetRemoveNulls = async (
  datasetId: string,
  payload: { attribute_id: string }
) => {
  try {
    const { data } = await axiosInstance.post(
      `/v1/datasets/${datasetId}/data/remove-nulls`,
      payload
    );
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const importDataset = async (
  form: FormData,
  signal?: AbortSignal,
  isShapefileUploadEnabled?: boolean
): Promise<{ operation_id: string }> => {
  try {
    const version = isShapefileUploadEnabled ? "v2" : "v1";
    const { data } = await axiosInstance.post(
      `/${version}/datasets/import`,
      form,
      {
        headers: {
          "Content-Type": "multipart/form-data"
        },
        signal
      }
    );
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const addImportDataset = async (
  id: string,
  form: FormData,
  signal?: AbortSignal,
  isShapefileUploadEnabled?: boolean
): Promise<{ operation_id: string }> => {
  try {
    const version = isShapefileUploadEnabled ? "v2" : "v1";
    const { data } = await axiosInstance.post(
      `/${version}/datasets/${id}/import`,
      form,
      {
        headers: {
          "Content-Type": "multipart/form-data"
        },
        signal
      }
    );
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const replaceImportDataset = async (
  id: string,
  form: FormData,
  signal?: AbortSignal,
  isShapefileUploadEnabled?: boolean
): Promise<{ operation_id: string }> => {
  try {
    const version = isShapefileUploadEnabled ? "v2" : "v1";
    const { data } = await axiosInstance.put(
      `/${version}/datasets/${id}/import`,
      form,
      {
        headers: {
          "Content-Type": "multipart/form-data"
        },
        signal
      }
    );
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const exportDataset = async (
  id: string,
  fileType: ExportDatasetFileType
): Promise<{ operation_id: string }> => {
  try {
    const { data } = await axiosInstance.get(`/v1/datasets/${id}/export`, {
      params: { file_type: fileType }
    });

    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const getOperationStatus = async (
  operationId: string
): Promise<OperationStatus> => {
  try {
    const { data } = await axiosInstance.get(
      `/v1/datasets/operations/${operationId}`
    );
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const patchDatasetSchema = async (body: DatasetSchemaPayload) => {
  const { id, ...payload } = body;
  try {
    const { data } = await axiosInstance.patch(
      `/v1/datasets/schema/${id}`,
      payload
    );
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const postDatasetAnalysis = async (
  body: DatasetAnalysisPayload
): Promise<{ id: string }> => {
  try {
    const { data } = await axiosInstance.post(`/v1/analysis`, body);
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const uploadDataset = async (file: FormData): Promise<any> => {
  try {
    const { data } = await axiosInstance.post(`/v1/datasets/upload`, file, {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    });
    return data;
  } catch (error) {
    const response = error as AxiosError;
    throw new CustomError(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (response?.response?.data as any)?.message || "",
      response?.response?.status || 500
    );
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getDatasetParsingStatus = async (): Promise<any> => {
  try {
    const { data } = await axiosInstance.get(`/v1/datasets/parsing`);
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const getDatasetDetail = async (id: string) => {
  try {
    const { data } = await axiosInstance.get(`/v1/datasets/${id}`);
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const downloadFile = async (id: string, name: string) => {
  try {
    const response: AxiosResponse<Blob> = await axiosInstance.get(
      `/v1/datasets/download/${id}`,
      { responseType: "blob" }
    );
    FileSaver.saveAs(response.data, `${name}.xlsx`);
    return name;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const patchDatasetMetadata = async (
  body: Partial<DatasetMetaDataPayload>
) => {
  try {
    const { id, ...payload } = body;

    const { data } = await axiosInstance.patch(
      `/v1/datasets/${id}`,
      removeEmptyParams(payload)
    );
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const deleteDataset = async (id: string) => {
  try {
    const { data } = await axiosInstance.delete(`/v1/datasets/${id}`);
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const getDatasetAttributeDistinctValues = async ({
  datasetId,
  attribute,
  filters
}: {
  datasetId: string;
  attribute: AttributeOption;
  filters?: DatasetFilterPayload[];
}) => {
  try {
    const params = new URLSearchParams();

    if (attribute.attribute_code) {
      params.append("attribute_code", attribute.attribute_code);
    }

    const queryString = params.toString() ? `?${params.toString()}` : "";

    const { data } = await axiosInstance.post(
      `/v1/datasets/${datasetId}/data/${removeStartFromUnderscore(attribute.id)}/distinct${queryString}`,
      filters
    );

    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    throw new CustomError(message, status);
  }
};

export const getAnalysisAttributeDistinctValues = async ({
  datasetId,
  attribute,
  analysisId
}: {
  datasetId: string;
  attribute: AttributeOption;
  analysisId: string;
}) => {
  try {
    const { data } = await axiosInstance.get(
      `/v1/analysis/${analysisId}/datasets/${datasetId}/data/${removeStartFromUnderscore(
        attribute.id
      )}/distinct`
    );

    return Array.isArray(data?.values)
      ? data.values
          .filter((v: unknown) => v !== null && v !== undefined)
          .map((value: unknown) => {
            const safeValue = String(value);
            return {
              display_name: safeValue,
              value: safeValue
            };
          })
      : [];
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    throw new CustomError(message, status);
  }
};

export const getAttributeValuesByAttributeId = async (params: {
  datasetId?: string;
  attribute?: AttributeOption | null;
  filters?: DatasetFilterPayload[];
  showAnalysisResultPopup?: boolean;
  latestAnalysisId?: string;
}) => {
  const {
    datasetId,
    attribute,
    filters,
    showAnalysisResultPopup,
    latestAnalysisId
  } = params;

  if (!datasetId || !attribute) {
    throw new Error("datasetId and attribute are required");
  }

  if (showAnalysisResultPopup && latestAnalysisId) {
    return getAnalysisAttributeDistinctValues({
      datasetId,
      attribute,
      analysisId: latestAnalysisId
    });
  }

  return getDatasetAttributeDistinctValues({
    datasetId,
    attribute,
    filters
  });
};

export const getDatasetByIdSchema = async (
  datasetId?: string,
  geometryType?: string
) => {
  if (!datasetId || !geometryType) {
    throw new Error("datasetId and geometryType are required");
  }
  try {
    const { data } = await axiosInstance.get(
      `/v1/datasets/${datasetId}/schema`,
      {
        params: { geometry_type: geometryType, data_type: "geometry" }
      }
    );
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const getSpatialAggregationTypes = async () => {
  try {
    const { data } = await axiosInstance.get("/v1/spatial-aggregation-types");
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const fetchDatasetMetadataBatch = async (
  datasetIds: Array<{
    id: string;
    spatialAggregationType: string;
  }>
) => {
  const metadataPromises = datasetIds.map(({ id, spatialAggregationType }) => {
    const aggregationConfig: H3Configuration | null =
      spatialAggregationType === "H3" ? { aggregationType: "AVG" } : null;
    return getDatasetMetadata(id, aggregationConfig);
  });

  return Promise.all(metadataPromises);
};

export const streamDatasetRawData = async (params: {
  datasetId: string;
  filterPayload?: DatasetFilterPayload[];
  h3Configuration?: H3Configuration | null;
  show_id?: boolean;
  limit?: number;
  generateDatasetData?: FetchDatasetStreamProps["generateDatasetData"];
}): Promise<DatasetData> => {
  const res = await fetchDatasetStream({
    datasetId: params.datasetId,
    filterPayload: params.filterPayload ?? [],
    h3Configuration: params.h3Configuration,
    show_id: params.show_id,
    limit: params.limit,
    generateDatasetData: params.generateDatasetData
  });
  if (!res.rawDataset) {
    throw new CustomError("Dataset stream returned no data", 500);
  }
  return res.rawDataset;
};

export const fetchDatasetDataBatch = async (
  datasetIds: Array<{
    id: string;
    filters: DatasetFilterPayload[];
    spatialAggregationType: string;
  }>
) => {
  const dataPromises = datasetIds.map(
    ({ id, filters, spatialAggregationType }) => {
      const aggregationConfig: H3Configuration | null =
        spatialAggregationType === "H3" ? { aggregationType: "AVG" } : null;

      return streamDatasetRawData({
        datasetId: id,
        filterPayload: filters,
        h3Configuration: aggregationConfig
      });
    }
  );

  return Promise.all(dataPromises);
};

export const getDataProviders = async () => {
  try {
    const { data } = await axiosInstance.get("/v1/datasets/providers");
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const deleteRowsDataset = async (
  datasetId: string,
  referenceIds: string[]
) => {
  try {
    const { data } = await axiosInstance.post(
      `/v1/datasets/${datasetId}/rows/delete`,
      { reference_ids: referenceIds }
    );
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const addRowsDataset = async (
  datasetId: string,
  rows: AddUpdateRowsDatasetPayload[],
  adm_area_source_attribute_name: string | null
) => {
  try {
    const { data } = await axiosInstance.post(
      `/v1/datasets/${datasetId}/rows`,
      { adm_area_source_attribute_name, rows }
    );
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const updateRowDataset = async (
  datasetId: string,
  rowId: string,
  rows: AddUpdateRowsDatasetPayload[],
  adm_area_source_attribute_name: string | null
) => {
  try {
    const { data } = await axiosInstance.put(
      `/v1/datasets/${datasetId}/rows/${rowId}`,
      { adm_area_source_attribute_name, rows }
    );
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const getDatasetStats = async (
  id: string,
  payload: DatasetStatsRequestPayload
): Promise<DatasetStatsResponse> => {
  try {
    const { data } = await axiosInstance.post(
      `/v1/datasets/${id}/stats`,
      payload
    );
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};
